/** „Passt dazu" in der Warenkorb-Schublade — siehe snippets/custom.cart-upsell.liquid. */
class CartUpsell extends HTMLElement {
  // Der Warenkorb-Morph aktualisiert Attribute am SELBEN Knoten —
  // connectedCallback feuert dann nicht. Gemessen: nach dem Plus-Klick blieb
  // die Liste leer, bis attributeChangedCallback das Rendern uebernahm.
  static observedAttributes = ['data-produkt', 'data-im-korb', 'data-fix', 'data-fueller', 'data-typen-im-korb', 'data-gewerbe'];
  attributeChangedCallback() {
    clearTimeout(this._t);
    this._t = setTimeout(() => this.render(), 60);
  }
  connectedCallback() { this.render(); }
  async render() {
    const produkt = this.dataset.produkt;
    const imKorb = new Set(this.dataset.imKorb.split(','));
    // Preisgrenze in Cent, 0 = keine Grenze. Ein Ein-Klick-Plus in der
    // Schublade ist ein Impulskauf; gemessen standen in den Ergaenzungslisten
    // Sparsets bis 152,99 EUR, die hier nichts verloren haben.
    const grenze = Number(this.dataset.maxPreis) || 0;
    // Produktarten, die schon im Korb liegen. Ein zweiter Tieflochmarker neben
    // dem ersten ist kein Zusatzkauf, sondern eine Dublette — die Familie
    // besteht aus mehreren Produkten, die ID-Pruefung allein greift da nicht.
    const typenImKorb = new Set(
      (this.dataset.typenImKorb || '').split('|').map((t) => t.trim().toLowerCase()).filter(Boolean),
    );
    const passt = (p) =>
      !imKorb.has(String(p.id)) &&
      (!grenze || p.price <= grenze) &&
      !typenImKorb.has(String(p.type || '').trim().toLowerCase());
    let daten = [];
    try {
      // Mit Sprach-Präfix (z. B. /fr-fr/), sonst liefert Shopify Titel und Links in der Hauptsprache (FR-Feedback 07.10.).
      const wurzel = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const a = await fetch(`${wurzel}recommendations/products.json?product_id=${produkt}&intent=complementary&limit=6`);
      daten = (await a.json()).products || [];
    } catch {
      // Kein `return`: Faellt die Empfehlungs-API aus, soll die
      // Haus-Empfehlung trotzdem stehen — sie braucht kein Netz.
    }
    let fix = [];
    try { fix = JSON.parse(this.dataset.fix || '[]'); } catch { /* leer lassen */ }
    let fueller = [];
    try { fueller = JSON.parse(this.dataset.fueller || '[]'); } catch { /* leer lassen */ }
    const geld = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
    // Die Kachel ist 48px, das Bild darf nicht 1000px sein. Die
    // Empfehlungs-API liefert die Bild-URL bereits mit `?v=…`, ein angehaengtes
    // `?width=120` waere dann die zweite Fragezeichen-Gruppe und wird
    // ignoriert — gemessen kamen so 800px und 1000px breite JPEGs herein. Der
    // Trenner muss sich also nach der URL richten.
    const klein = (url) => {
      if (!url) return '';
      if (/[?&]width=/.test(url)) return url;
      return url + (url.includes('?') ? '&' : '?') + 'width=120';
    };
    // Die Haus-Empfehlung belegt den DRITTEN Platz, nicht den ersten: Der erste
    // gehoert dem, was zum Artikel im Korb wirklich passt. Steht sie schon in
    // den Empfehlungen, wird sie dort entfernt, sonst waere sie doppelt drin.
    const haus = fix.find((p) => passt(p));
    const empfehlungen = daten.filter((p) => p.available && passt(p) && p.id !== haus?.id);
    const kandidaten = haus ? [...empfehlungen.slice(0, 2), haus] : empfehlungen.slice(0, 3);
    // Freie Plaetze mit Bestsellern auffuellen. Das aendert nichts an den
    // Plaetzen davor: Was zum Artikel im Korb passt, steht weiterhin vorne,
    // die Haus-Empfehlung weiterhin auf dem dritten Platz. Aufgefuellt wird
    // nur, was sonst leer bliebe — bei Produkten ohne Ergaenzungsliste ist das
    // der Normalfall.
    if (kandidaten.length < 3) {
      const drin = new Set(kandidaten.map((p) => String(p.id)));
      // Auch die schon gewaehlten Produktarten merken. Sonst stand neben der
      // Haus-Empfehlung „Tieflochmarker SPOTIX" der Bestseller „Tieflochmarker
      // SPOTIX Set" — zwei Kacheln, dasselbe Werkzeug. Gegen den Warenkorb
      // filtert `passt` bereits, gegeneinander bisher nicht.
      const artenDrin = new Set(
        kandidaten.map((p) => String(p.type || '').trim().toLowerCase()).filter(Boolean),
      );
      for (const p of fueller) {
        if (kandidaten.length >= 3) break;
        const art = String(p.type || '').trim().toLowerCase();
        if (drin.has(String(p.id)) || !passt(p)) continue;
        if (art && artenDrin.has(art)) continue;
        drin.add(String(p.id));
        if (art) artenDrin.add(art);
        kandidaten.push(p);
      }
    }
    if (!kandidaten.length) { this.hidden = true; return; }
    // Gewerbekunden (13.10.2026): Gewerbepreis wie snippets/custom.gewerbepreis.liquid
    // und die Function „gewerbepreis": netto × (100 − prozent) ÷ 100 kaufmännisch,
    // plus Cent-Korrektur der Variante. data-gewerbe fehlt, wenn er nicht gilt.
    // Korrektur [Nettopreis, Cent] gilt nur bei genau diesem Preis (14.10.2026,
    // Österreich hat andere Nettopreise), eine einzelne Zahl immer.
    let gewerbe = null;
    try { gewerbe = JSON.parse(this.dataset.gewerbe || 'null'); } catch { /* kein Gewerbepreis */ }
    const anzeigePreis = (p) => {
      if (!gewerbe || !(gewerbe.prozent > 0 && gewerbe.prozent < 50)) return p.price;
      const tags = (p.tags || []).map((t) => String(t).toLowerCase());
      if (tags.some((t) => t === 'porto' || t === 'versandkosten')) return p.price;
      const variante = String(p.variant ?? p.variants?.[0]?.id ?? '');
      const e = gewerbe.korrektur?.[variante];
      const k = Array.isArray(e) ? (Number(e[0]) === p.price ? Number(e[1]) : 0) : Number(e ?? 0);
      const roh = Math.floor((p.price * (100 - gewerbe.prozent)) / 100 + 0.5 + 1e-9);
      return Math.min(p.price, Math.max(0, roh + (Number.isInteger(k) && Math.abs(k) <= 5 ? k : 0)));
    };
    // Zuruecksetzen ist Pflicht: Der Baustein wird nach jedem Morph neu
    // gerendert, und ein `hidden` aus einem frueheren Durchlauf bliebe sonst
    // stehen — die Karten waeren im Markup da und unsichtbar.
    this.hidden = false;
    this.innerHTML = `<p class="custom-cart-upsell__titel">${this.dataset.ueberschrift}</p>` +
      kandidaten.map((p) => `
        <div class="custom-cart-upsell__karte">
          <img src="${klein(p.featured_image)}" alt="" width="48" height="48" loading="lazy">
          <a class="custom-cart-upsell__name" href="${p.url}">${p.title.replace('PRESCH ', '')}</a>
          <span class="custom-cart-upsell__preis">${geld.format(anzeigePreis(p) / 100)}</span>
          <button type="button" class="custom-cart-upsell__plus" aria-label="${this.dataset.labelHinzu || 'In den Warenkorb'}: ${p.title}"
            data-variante="${p.variant ?? p.variants[0].id}">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M8 3v10M3 8h10"/></svg>
          </button>
        </div>`).join('');
    this.querySelectorAll('[data-variante]').forEach((b) =>
      b.addEventListener('click', async () => {
        b.disabled = true;
        const update = window.Shopify?.actions?.updateCart;
        try {
          if (typeof update === 'function') {
            await update({ action: 'add', lines: [{ merchandiseId: Number(b.dataset.variante), quantity: 1 }] });
          } else {
            await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(b.dataset.variante), quantity: 1 }] }) });
            location.reload();
          }
        } catch { b.disabled = false; }
      })
    );
  }
}
if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
